import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Search, Check, X } from 'lucide-react'
import { getCoinIcon } from '@/lib/common'
import type { TokenInfo } from '@/types'
import { cn } from '@/lib/utils'

interface TokenSelectorProps {
  open: boolean
  onClose: () => void
  onSelect: (token: TokenInfo) => void
  coins: TokenInfo[]
  selected?: TokenInfo | null
  balances?: Record<string, string>
}

export function TokenSelector({ open, onClose, onSelect, coins, selected, balances }: TokenSelectorProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const filteredCoins = useMemo(() => {
    if (!search) return coins
    const keyword = search.toLowerCase()
    return coins.filter((c) => c.coin.toLowerCase().includes(keyword))
  }, [coins, search])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 w-[350px] max-h-[70vh] bg-white rounded-2xl shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <span className="text-[16px] font-semibold text-foreground">{t('swap.selectToken')}</span>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
            <X className="h-5 w-5 text-nav" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pb-3">
          <div className="flex items-center h-[40px] bg-[#f5f6fa] rounded-full px-4 gap-2">
            <Search className="h-4 w-4 text-nav shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="flex-1 bg-transparent text-[14px] text-foreground outline-none placeholder:text-nav"
            />
          </div>
        </div>

        {/* Token List */}
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {filteredCoins.length === 0 ? (
            <div className="py-10 text-center text-[14px] text-nav">{t('public.noData')}</div>
          ) : (
            filteredCoins.map((coin) => {
              const isSelected = selected?.tokenId === coin.tokenId
              const balance = balances?.[coin.tokenId]
              return (
                <div
                  key={coin.tokenId}
                  onClick={() => { onSelect(coin); onClose() }}
                  className={cn(
                    'flex items-center px-3 py-3 rounded-xl cursor-pointer transition-colors hover:bg-[#edf5ed]',
                    isSelected && 'bg-[#edf5ed]',
                  )}
                >
                  <img
                    src={getCoinIcon(coin.contract, coin.coin)}
                    alt={coin.coin}
                    className="w-[36px] h-[36px] rounded-full mr-3 bg-gray-100"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-semibold text-foreground">{coin.coin}</div>
                    <div className="text-[12px] text-nav truncate">{coin.contract}</div>
                  </div>
                  {balance && (
                    <span className="text-[14px] text-foreground mr-2">{balance}</span>
                  )}
                  {isSelected && <Check className="h-5 w-5 text-primary shrink-0" />}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
